/**
 * WorkDocFindReplace — Search and replace overlay for the editor.
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, ChevronDown, ChevronUp, Replace } from "lucide-react";

export default function WorkDocFindReplace({ editor, open, onClose }) {
  const [searchText, setSearchText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [results, setResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const clearHighlights = useCallback(() => {
    if (!editor) return;
    const { doc } = editor.state;
    const marksToRemove = [];
    doc.descendants((node, pos) => {
      if (node.marks) {
        node.marks.forEach((mark, idx) => {
          if (mark.type.name === "searchHighlight") {
            marksToRemove.push({ pos, idx });
          }
        });
      }
    });
    // Remove search highlights by clearing marks
    marksToRemove.reverse().forEach(({ pos }) => {
      editor.chain().focus().setTextSelection(pos).unsetMark("searchHighlight").run();
    });
  }, [editor]);

  const doSearch = useCallback(() => {
    if (!editor || !searchText) {
      setResults([]);
      return;
    }
    clearHighlights();
    const text = editor.state.doc.textContent;
    const matches = [];
    let idx = 0;
    let pos = 0;
    const lowerText = text.toLowerCase();
    const lowerSearch = searchText.toLowerCase();
    while ((idx = lowerText.indexOf(lowerSearch, pos)) !== -1) {
      matches.push({
        index: idx,
        endIndex: idx + searchText.length,
        text: text.substring(idx, idx + searchText.length),
      });
      pos = idx + 1;
    }
    setResults(matches);
    setCurrentIndex(matches.length > 0 ? 0 : -1);

    // Navigate to first match
    if (matches.length > 0 && editor) {
      const match = matches[0];
      editor.chain().focus().setTextSelection({ from: match.index + 1, to: match.endIndex + 1 }).run();
    }
  }, [editor, searchText, clearHighlights]);

  const goToMatch = (index) => {
    if (!editor || results.length === 0) return;
    const match = results[index];
    editor.chain().focus().setTextSelection({ from: match.index + 1, to: match.endIndex + 1 }).run();
    setCurrentIndex(index);
  };

  const goNext = () => {
    if (results.length === 0) return;
    goToMatch((currentIndex + 1) % results.length);
  };

  const goPrev = () => {
    if (results.length === 0) return;
    goToMatch((currentIndex - 1 + results.length) % results.length);
  };

  const replaceOne = () => {
    if (!editor || currentIndex < 0 || currentIndex >= results.length) return;
    const match = results[currentIndex];
    editor.chain().focus().setTextSelection({ from: match.index + 1, to: match.endIndex + 1 }).deleteSelection().insertContent(replaceText).run();
    doSearch();
  };

  const replaceAll = () => {
    if (!editor || results.length === 0) return;
    // Replace from last to first to preserve positions
    let offset = 0;
    for (let i = results.length - 1; i >= 0; i--) {
      const match = results[i];
      editor.chain().focus().setTextSelection({ from: match.index + 1 + offset, to: match.endIndex + 1 + offset }).deleteSelection().insertContent(replaceText).run();
      offset += replaceText.length - searchText.length;
    }
    doSearch();
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => doSearch(), 100);
    } else {
      clearHighlights();
    }
  }, [open, searchText]);

  if (!open) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-850 border-b border-gray-700 text-xs">
      <div className="flex items-center gap-1">
        <Input
          type="text"
          placeholder="Find..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && doSearch()}
          className="w-36 h-7 text-xs bg-gray-900 border-gray-700 text-white rounded"
        />
        <Input
          type="text"
          placeholder="Replace..."
          value={replaceText}
          onChange={(e) => setReplaceText(e.target.value)}
          className="w-36 h-7 text-xs bg-gray-900 border-gray-700 text-white rounded"
        />
      </div>

      <span className="text-gray-500">
        {results.length > 0
          ? `${currentIndex + 1}/${results.length}`
          : "0/0"}
      </span>

      <div className="flex items-center gap-0.5">
        <Button variant="ghost" size="sm" onClick={goPrev} disabled={results.length === 0} className="h-6 w-6 p-0 text-gray-400 hover:text-white">
          <ChevronUp className="w-3 h-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={goNext} disabled={results.length === 0} className="h-6 w-6 p-0 text-gray-400 hover:text-white">
          <ChevronDown className="w-3 h-3" />
        </Button>
      </div>

      <Button variant="ghost" size="sm" onClick={replaceOne} disabled={results.length === 0} className="h-7 px-2 text-xs text-gray-400 hover:text-white">
        <Replace className="w-3 h-3 mr-1" /> One
      </Button>
      <Button variant="ghost" size="sm" onClick={replaceAll} disabled={results.length === 0} className="h-7 px-2 text-xs text-gray-400 hover:text-white">
        <Replace className="w-3 h-3 mr-1" /> All
      </Button>

      <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0 text-gray-400 hover:text-white ml-auto">
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
}
